import {
  createElement,
  createContext,
  useReducer,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { PreferenceItem } from '@sas/nova-core/services/preferences';
import { StoreState, storeAction, storeReducer } from './StoreReducer';
import { StoreActions } from './StoreActionConstants';
import { useGetSettings } from '../hooks/useGetSettings';
import { BusyStateIndicator } from '../../BusyStateIndicator';
import { useMessageDialogText, MessageDialog } from '../../MessageDialog';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { SettingsChangeEventInfo, fireSettingsChange } from '../events/SettingsChangeEvent';
import { useConfig } from '../../../hooks/useConfig';
import useForceUpdate from '../../../hooks/useForceUpdate';
import { GlobalSettingsKeys, UI5_NO_COLOR_VALUE } from '..';
import { useMounted } from '../../../hooks/useMounted';
import { useSetPreferences } from '../__internal__/hooks/useSetPreferences';
import { useDeletePreferences } from '../__internal__/hooks/useDeletePreferences';

type SettingsStoreContext = {
  get: (key: string) => string | undefined;
  update: (data: PreferenceItem, isDirty: boolean) => void;
  reset: (keys: Array<string>) => void;
  setDefaultValue: (key: string, value: string) => void;
  addFieldValueChangeHandler(
    key: string,
    fn: (value: string | undefined, isDirty: boolean) => void
  ): void;
  removeFieldValueChangeHandler(
    key: string,
    fn: (value: string | undefined, isDirty: boolean) => void
  ): void;
  addFormStateChangeListener(fn: (shouldReset: boolean) => void, keys: Array<string>): void;
  removeFormStateChangeListener(fn: (shouldReset: boolean) => void): void;
  hasDirtyFields: () => boolean;
  clearDirtyFields: () => void;
};

const SettingsStoreContext = createContext<SettingsStoreContext | undefined>(undefined);

export function useSettingsStore() {
  const store = useContext(SettingsStoreContext);
  if (store === undefined) {
    throw new Error('The settings store must be used within SettingsStore Provider');
  }
  return store;
}

export function useSettingsReset(fields: Array<string>): [boolean, () => void] {
  const service = useSettingsStore();

  const [value, setValue] = useState<boolean>(() => {
    return fields.some((field) => service.get(field) !== undefined);
  });

  useEffect(() => {
    function callback(value: boolean) {
      setValue(value);
    }
    service.addFormStateChangeListener(callback, fields);
    return () => {
      service.removeFormStateChangeListener(callback);
    };
  }, [fields, service]);

  const reset = useCallback(() => {
    service.reset(fields);
  }, [fields, service]);

  return [value, reset];
}

export function useSettingsField(
  field: string,
  defaultValue?: string
): [string | undefined, (value: string, isDirty?: boolean) => void, () => void] {
  const service = useSettingsStore();
  const [value, setValue] = useState<string | undefined>(() => {
    return service.get(field);
  });
  const isDirtyRef = useRef(false);
  const rerender = useForceUpdate();

  useEffect(() => {
    function callback(_value: string | undefined, _isDirty: boolean) {
      if (!_isDirty) {
        // Switching to new Group when the current group field is in error will force
        // correct the dirty field.
        isDirtyRef.current = false;
        setValue(_value);
        rerender();
      }
    }

    if (defaultValue) {
      service.setDefaultValue(field, defaultValue);
    }

    service.addFieldValueChangeHandler(field, callback);
    return () => {
      service.removeFieldValueChangeHandler(field, callback);
    };
  }, [field, defaultValue, service, rerender]);
  /*
    Earlier the idea was to invoke the setValue mutator only
    following the success of save or reset operation. But we found
    that in controls like Input, for any user interaction change, if the
    changed value is updated in the React state in a delayed fashion,
    React behaves(we noticed unwanted curosr jumps) as if there was a
    switch from Controlled to UnControlled component.
  */
  const updateValue = useCallback(
    (_value: string, isDirty = false) => {
      isDirtyRef.current = isDirty;

      if (!isDirty && (_value === '' || _value === defaultValue)) {
        setValue(undefined);
        service.reset([field]);
      } else {
        setValue(_value);
        service.update({ id: field, value: _value }, isDirty);
      }
      rerender();
    },
    [field, service, defaultValue, rerender]
  );

  const reset = useCallback(() => {
    setValue(undefined);
    service.reset([field]);
  }, [field, service]);

  /*
    I did not feel appropriate to have default value set upon the state.
    It harms the use-cases Where the default value could change dynamically
    like the focusColor, focusThickness and focusOpacity fields in Accessibility form.
  */
  return [isDirtyRef.current ? value : value || defaultValue, updateValue, reset];
}

type T0 = Parameters<SettingsStoreContext['addFieldValueChangeHandler']>;

type T1 = Parameters<SettingsStoreContext['addFormStateChangeListener']>;

export interface SettingsStoreProviderProps {}

export function SettingsStoreProvider(props: React.PropsWithChildren<SettingsStoreProviderProps>) {
  const currentAppId = useConfig()?.get('appId');
  const [storeState, storeDispatch] = useReducer<React.Reducer<StoreState, storeAction>>(
    storeReducer,
    {
      data: new Map(),
      isReadError: false,
      isUpdateError: false,
      isDeleteError: false,
    }
  );

  // this is never re-assigned a new value.
  const dirtyFieldsRef = useRef<Map<string, string>>(new Map());

  const defaultValuesRef = useRef<Map<string, string>>(new Map());

  const fieldValueChangeHandlers = useRef<Map<T0[0], Array<T0[1]>>>(new Map());

  const formStateChangeHandlers = useRef<Map<T1[0], T1[1]>>(new Map());

  const runChangeHandlers = useCallback(
    (change: Array<PreferenceItem>) => {
      const prefObj = change.reduce(
        (prefObj: { [key: string]: string | undefined }, preference) => ({
          ...prefObj,
          [preference.id]: preference.value,
        }),
        {}
      );

      const changedKeys = Object.keys(prefObj);

      // form reset change
      for (const [fn, keys] of formStateChangeHandlers.current) {
        const isImpacted = changedKeys.some((changedKey) => keys.indexOf(changedKey) >= 0);
        if (isImpacted) {
          const enableReset = keys.some((key) => {
            if (!dirtyFieldsRef.current.has(key)) {
              const fieldValue =
                changedKeys.indexOf(key) >= 0 ? prefObj[key] : storeState.data.get(key);

              if (!fieldValue || fieldValue === defaultValuesRef.current.get(key)) {
                return false;
              }
            }
            return true;
          });
          fn(enableReset);
        }
      }

      // field value change
      for (const [key, callbacks] of fieldValueChangeHandlers.current) {
        if (changedKeys.indexOf(key) >= 0) {
          callbacks.forEach((callback) => callback(prefObj[key], dirtyFieldsRef.current.has(key)));
        }
      }
    },
    [storeState.data]
  );

  const { CLOSE } = useMessageDialogText();
  const localize = useNCTextLocalizer('Settings');

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [fetchPending, setFetchPending] = useState<boolean>(true);
  const [remoteInvocationCount, setRemoteInvocationCount] = useState(0);
  const mounted = useMounted();

  const resolveGetSettings = useCallback(
    (items: Array<PreferenceItem>) => {
      setFetchPending(false);

      items = items.reduce((accumulator: Array<PreferenceItem>, item: PreferenceItem) => {
        if (!(item.id === GlobalSettingsKeys.FOCUS_COLOR && item.value === UI5_NO_COLOR_VALUE)) {
          accumulator.push(item);
        }
        return accumulator;
      }, []);

      runChangeHandlers(items);

      storeDispatch({
        type: StoreActions.READ_SUCCESS,
        payload: items,
      });
    },
    [runChangeHandlers]
  );

  const rejectGetSettings = useCallback(() => {
    setFetchPending(false);

    storeDispatch({
      type: StoreActions.ERROR_ON_READ,
    });
  }, []);

  const resolveSetSettings = useCallback(
    (items: Array<PreferenceItem>) => {
      if (!mounted.current) return;

      setRemoteInvocationCount((prev) => prev - 1);

      const eventChange = items.reduce((preferenceChanges, item) => {
        return preferenceChanges.concat([
          {
            key: item.id,
            value: item.value,
            oldValue: storeState.data.get(item.id),
          },
        ]);
      }, [] as SettingsChangeEventInfo);

      storeDispatch({
        type: StoreActions.UPDATE_SUCCESS,
        payload: items,
      });

      fireSettingsChange(eventChange, currentAppId);
    },
    [storeState.data, currentAppId, mounted]
  );

  const rejectSetSettings = useCallback(
    (items?: Array<PreferenceItem>) => {
      if (!mounted.current) return;

      setRemoteInvocationCount((prev) => prev - 1);

      if (items) {
        const stateToBeReversed: Array<PreferenceItem> = items.map((item) => ({
          id: item.id,
          value: storeState.data.get(item.id) || '',
        }));

        runChangeHandlers(stateToBeReversed);
      }

      storeDispatch({
        type: StoreActions.ERROR_ON_UPDATE,
      });
    },
    [runChangeHandlers, storeState.data, mounted]
  );

  const resolveDeleteSettings = useCallback(
    (keys: Array<string>) => {
      if (!mounted.current) return;

      setRemoteInvocationCount((prev) => prev - 1);

      const [storeUpdate, eventChange] = keys.reduce(
        ([storeUpdate, eventChange], key) => {
          return [
            storeUpdate.concat([
              {
                id: key,
                value: '',
              },
            ]),
            eventChange.concat([
              {
                key,
                value: undefined,
                oldValue: storeState.data.get(key),
              },
            ]),
          ];
        },
        [[] as Array<PreferenceItem>, [] as SettingsChangeEventInfo]
      );

      storeDispatch({
        type: StoreActions.DELETE_SUCCESS,
        payload: storeUpdate,
      });

      fireSettingsChange(eventChange, currentAppId);
    },
    [storeState.data, currentAppId, mounted]
  );

  const rejectDeleteSettings = useCallback(
    (keys: Array<string>) => {
      if (!mounted.current) return;

      setRemoteInvocationCount((prev) => prev - 1);

      const stateToBeReversed: Array<PreferenceItem> = keys.map((key) => ({
        id: key,
        value: storeState.data.get(key) || '',
      }));

      runChangeHandlers(stateToBeReversed);

      storeDispatch({
        type: StoreActions.ERROR_ON_DELETE,
      });
    },
    [runChangeHandlers, storeState.data, mounted]
  );

  useGetSettings(resolveGetSettings, rejectGetSettings);

  const updateSettingsInvoker = useSetPreferences(resolveSetSettings, rejectSetSettings);

  const deleteSettingsInvoker = useDeletePreferences(resolveDeleteSettings, rejectDeleteSettings);

  const service = useMemo(
    () => ({
      get: (key: string) => {
        return storeState.data.get(key);
      },

      update: (item: PreferenceItem, isDirty = false) => {
        if (isDirty) {
          dirtyFieldsRef.current.set(item.id, item.value);
        } else {
          dirtyFieldsRef.current.delete(item.id);
          setRemoteInvocationCount((prev) => prev + 1);
          updateSettingsInvoker([item]);
        }
        runChangeHandlers([{ id: item.id, value: item.value }]);
      },

      reset: (keys: Array<string>) => {
        keys.map((key) => dirtyFieldsRef.current.delete(key));
        setRemoteInvocationCount((prev) => prev + 1);
        deleteSettingsInvoker(keys);
        runChangeHandlers(keys.map((key) => ({ id: key, value: '' })));
      },

      // for a change in default value, we need to re-invalidate the specific form
      // Reset button status. This is a special case for custom focus attributes, whose
      // default values change on theme change.
      setDefaultValue: (key: string, value: string) => {
        const oldDefault = defaultValuesRef.current.get(key);
        defaultValuesRef.current.set(key, value);
        if (oldDefault && oldDefault !== value) {
          runChangeHandlers([{ id: key, value: storeState.data.get(key) || '' }]);
        }
      },

      addFieldValueChangeHandler: (key: T0[0], fn: T0[1]) => {
        const callbacks = fieldValueChangeHandlers.current.get(key) || [];
        fieldValueChangeHandlers.current.set(key, [...callbacks, fn]);
      },

      removeFieldValueChangeHandler: (key: T0[0], fn: T0[1]) => {
        const callbacks = fieldValueChangeHandlers.current.get(key);
        if (Array.isArray(callbacks)) {
          fieldValueChangeHandlers.current.set(
            key,
            callbacks.filter((callback) => callback !== fn)
          );
        }
      },

      addFormStateChangeListener: (fn: T1[0], supportedKeys: T1[1]) => {
        formStateChangeHandlers.current.set(fn, supportedKeys);
      },

      removeFormStateChangeListener: (fn: T1[0]) => {
        formStateChangeHandlers.current.delete(fn);
      },

      hasDirtyFields: () => dirtyFieldsRef.current.size > 0,

      clearDirtyFields: () => {
        const change: Array<PreferenceItem> = [];
        for (const key of dirtyFieldsRef.current.keys()) {
          change.push({ id: key, value: storeState.data.get(key) || '' });
        }
        dirtyFieldsRef.current.clear();
        runChangeHandlers(change);
      },
    }),
    [storeState.data, runChangeHandlers, updateSettingsInvoker, deleteSettingsInvoker]
  );

  // The error message for update and reset is the same.
  const isErrorMessageDialogOpen =
    storeState.isDeleteError || storeState.isReadError || storeState.isUpdateError;

  const errorMessageDialogDetails = isErrorMessageDialogOpen
    ? storeState.isReadError
      ? {
          title: localize('settings.fetch.error.dialog.title'),
          text: localize('settings.fetch.error.msg'),
        }
      : storeState.isUpdateError
      ? {
          title: localize('settings.save.error.dialog.title'),
          text: localize('settings.save.error.msg'),
        }
      : storeState.isDeleteError
      ? {
          title: localize('settings.reset.error.dialog.title'),
          text: localize('settings.reset.error.msg'),
        }
      : { title: '', text: '' }
    : { title: '', text: '' };

  /**
   * NOVACOMMONS-15508 - The react focus lock version upgrade seems to have had an impact here.
   * If the Error Dialog is activated in the same render cycle as the busy indicator
   * is turned off, the focus lock seems to capture the busy indicator as the
   * original focus element to return focus which causes the focus to be lost after the Error
   * Dialog is closed. Hence this strategy of displaying Error Dialog in a separate render
   * cycle after the busy indicator is turned off.
   */
  useEffect(() => {
    if (isErrorMessageDialogOpen) {
      setShowErrorDialog(true);
    } else {
      setShowErrorDialog(false);
    }
  }, [isErrorMessageDialogOpen]);

  return (
    <SettingsStoreContext.Provider value={service}>
      <BusyStateIndicator fillParentContainer active={remoteInvocationCount > 0 || fetchPending}>
        {props.children}
        <MessageDialog
          type="error"
          isOpen={showErrorDialog}
          title={errorMessageDialogDetails.title}
          text={errorMessageDialogDetails.text}
          dismissAction={{
            text: CLOSE,
            onPress: () => {
              storeDispatch({ type: StoreActions.CLEAR_ERROR });
            },
            isDefault: true,
          }}
        />
      </BusyStateIndicator>
    </SettingsStoreContext.Provider>
  );
}
